import { compactFlatten, isString, object } from '@art-suite/art-core-ts';
import { css } from '@emotion/react';
import DownloadIcon from '@mui/icons-material/Download';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { downloadBlob, usePageTitle } from '../../Lib';

import { ApiOrgProduct, getScanInlineProductFromOrgProduct } from '@kaiizen/kai-pro-api';
import {
  getNormalizedScanTemplateRoot,
  getScanTemplatePart,
  getScanTemplatePartType,
  getTemplateTaskIdToGroupsMap,
  getTemplateWithInlineProducts,
  ScanTemplateGroup,
  ScanTemplatePartContext,
  ScanTemplateRoot,
  ScanTemplateTask,
} from '@kaiizen/kai-scan-template-system';

import { useAuth } from '@kaiizen/kai-pro-api-react-state';
import { ErrorBoundary } from '../../Components';
import { PageLayout } from '../../Layout';
import {
  AllTemplateTasksEditor,
  createScanTemplateSearchCache,
  ScanTemplateGroupEditor,
  ScanTemplateNavPanel,
  ScanTemplateOverview,
  ScanTemplatePartEditorPanel,
} from '../../Partials';
import { getScanTemplateFilterFunctions } from '../../Partials/ScanTemplateEditor';
import { useAutoSwitchOrgForResource, useOrgProductsByIdForTemplate, useScanTemplate } from '../../State';
import { COLORS2, LAYOUT2 } from '../../Styles';
import { FlyOut } from '../../Widgets';
import { GenericLoadingPage } from '../GenericPages';

const TYPE_DISPLAY_NAME = {
  scanTemplate: 'Root',
  groupTemplate: 'Group',
  taskTemplate: 'Task',
  optionTemplate: 'Option',
  lineItemTemplate: 'Line Item',
};

const PANEL_CONTENT_STYLE = css`
  label: PANEL_CONTENT_STYLE;
  display: flex;
  height: 100%;
  min-height: 0;
  background-color: ${COLORS2.alwaysDark.background.dark};
`;

const LEFT_PANEL_STYLE = css`
  label: LEFT_PANEL_STYLE;
  width: ${LAYOUT2.leftNavWidth}px;
  min-width: 250px;
  overscroll-behavior: contain;
  align-self: stretch;
`;

const SCROLLABLE_CONTENT_STYLE = css`
  label: SCROLLABLE_CONTENT_STYLE;
  flex: 1;
  overflow: auto;
  min-height: 0;
`;

const MAIN_CONTENT_STYLE = css`
  label: MAIN_CONTENT_STYLE;
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: auto;
  min-height: 0;
  min-width: 340px;
`;

type CurrentView = { type: 'overview' } | { type: 'all-tasks' } | { type: 'group'; groupId: string };

const getExportedTemplateString = (template: ScanTemplateRoot, orgProductsById: Record<string, ApiOrgProduct>): string => {
  const inlineProductsById = object(orgProductsById, getScanInlineProductFromOrgProduct);
  const templateWithInlineProducts = getTemplateWithInlineProducts(template, inlineProductsById);
  return JSON.stringify(templateWithInlineProducts, null, 2);
};

// Helper functions to convert between URL parameters and ScanTemplatePartContext
const createScanTemplatePartContextFromUrlParams = (searchParams: URLSearchParams): ScanTemplatePartContext | null => {
  const groupId = searchParams.get('groupId');
  const taskId = searchParams.get('taskId') ?? undefined;
  const optionId = searchParams.get('optionId');
  const lineItemId = searchParams.get('lineItemId');

  if (!taskId && !groupId) return null;

  return {
    taskId,
    ...(groupId && { groupId }),
    ...(optionId && { optionId }),
    ...(lineItemId && { lineItemId }),
  };
};
const createUrlParamsFromScanTemplatePartContext = (context: ScanTemplatePartContext): Record<string, string> => {
  const params: Record<string, string> = {};
  if (context.groupId) params.groupId = context.groupId;
  if (context.taskId) params.taskId = context.taskId;
  if (context.optionId) params.optionId = context.optionId;
  if (context.lineItemId) params.lineItemId = context.lineItemId;
  return params;
};

const EMPTY_GROUPS: ScanTemplateGroup[] = [];
const EMPTY_TASK_TEMPLATES_BY_ID: Record<string, ScanTemplateTask> = {};
export const ScanTemplatePage = ({ scanTemplateId, allTasks, groupId }: { scanTemplateId: string; allTasks?: boolean; groupId?: string }) => {
  const { userCan } = useAuth();
  const readOnly = !userCan('update', 'ScanTemplate');
  const { scanTemplate, scanTemplateData, scanTemplateDataMutators, scanTemplateMutators, isError, error } = useScanTemplate(scanTemplateId);
  usePageTitle(scanTemplate?.name ? `Template · ${scanTemplate.name}` : undefined);
  const [scanTemplateRoot, setScanTemplateRoot] = useState<ScanTemplateRoot | undefined>(undefined);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [filter, setFilter] = useState('');
  const currentView = groupId ? 'group' : allTasks ? 'all-tasks' : 'overview';
  const searchCache = useMemo(() => createScanTemplateSearchCache(), []);

  useAutoSwitchOrgForResource({
    error: isError ? error : undefined,
    scanTemplateId,
  });

  const selectedScanTemplatePartContext = createScanTemplatePartContextFromUrlParams(searchParams);
  const selectedScanTemplatePart = selectedScanTemplatePartContext && scanTemplateRoot
    ? getScanTemplatePart(scanTemplateRoot, selectedScanTemplatePartContext)
    : undefined;

  const setSelectedScanTemplatePartContext = (context: ScanTemplatePartContext | null) => {
    if (context) {
      const params = createUrlParamsFromScanTemplatePartContext(context);
      setSearchParams(params);
    } else {
      setSearchParams({});
    }
  };

  const taskIdToGroupsMap = useMemo(() => (scanTemplateRoot ? getTemplateTaskIdToGroupsMap(scanTemplateRoot) : {}), [scanTemplateRoot]);
  const { orgProductsById } = useOrgProductsByIdForTemplate(scanTemplateId);

  const filterRegex = useMemo(() => {
    if (!filter) return undefined;
    return new RegExp(filter.trim().replace(/\s+/g, '.*'), 'i');
  }, [filter]);

  const scanTemplateFilterFunctions = useMemo(() => getScanTemplateFilterFunctions(scanTemplateRoot?.taskTemplatesById ?? {}, searchCache, filterRegex), [
    scanTemplateRoot?.taskTemplatesById,
    searchCache,
    filterRegex,
  ]);

  useEffect(() => {
    if (scanTemplateData) {
      setScanTemplateRoot(getNormalizedScanTemplateRoot(scanTemplateData));
    }
  }, [scanTemplateData]);

  const setShowingAllTasks = useCallback(() => navigate(`/template/${scanTemplateId}/all-tasks`), [navigate, scanTemplateId]);
  const setShowingTemplateOverview = useCallback(() => navigate(`/template/${scanTemplateId}`), [navigate, scanTemplateId]);
  const setShowingClassicTasks = useCallback(() => navigate(`/template/${scanTemplateId}/classic`), [navigate, scanTemplateId]);
  const handleSetCurrentGroupId = useCallback(
    (groupId: string | undefined) => navigate(groupId ? `/template/${scanTemplateId}/group/${groupId}` : `/template/${scanTemplateId}`),
    [navigate, scanTemplateId],
  );

  const groupTemplates = scanTemplateRoot?.groupTemplates ?? EMPTY_GROUPS;
  const taskTemplatesById = scanTemplateRoot?.taskTemplatesById ?? EMPTY_TASK_TEMPLATES_BY_ID;
  const sortedTasks = useMemo(() => [...Object.values(taskTemplatesById)].sort((a, b) => a.name.localeCompare(b.name)), [taskTemplatesById]);
  const filteredTasks = useMemo(() => sortedTasks.filter(scanTemplateFilterFunctions.filterScanTemplateTask), [sortedTasks, scanTemplateFilterFunctions]);
  const filteredTasksById = useMemo(() => object(filteredTasks, { withKey: ({ id }) => id }), [filteredTasks]);
  const filteredGroups = useMemo(() => groupTemplates.filter(scanTemplateFilterFunctions.filterScanTemplateGroup), [
    groupTemplates,
    scanTemplateFilterFunctions,
  ]);
  const filteredTasksByGroupIds = useMemo(
    () =>
      object(filteredGroups, {
        withKey: ({ id }) => id,
        with: (group: ScanTemplateGroup) => group.taskTemplateIds?.filter((id: string) => !!filteredTasksById[id]) ?? [],
      }),
    [filteredGroups, filteredTasksById],
  );
  const groupCategories = useMemo(() => [...new Set(compactFlatten(filteredGroups.map(group => group.category || 'other') ?? []))].filter(isString).sort(), [
    filteredGroups,
  ]);
  const taskCategories = useMemo(() => [...new Set(sortedTasks.map(task => task.category || 'other'))].sort(), [sortedTasks]);

  if (!scanTemplate || !scanTemplateData || !scanTemplateRoot) {
    return <GenericLoadingPage loadingMessage={isError ? 'Error loading scan template' : undefined} />;
  }

  const downloadTemplateReady = !!orgProductsById;

  const downloadTemplate = () => {
    if (!orgProductsById) throw new Error('orgProductsById is undefined');
    const dataStr = getExportedTemplateString(scanTemplateRoot, orgProductsById ?? {});
    downloadBlob(new Blob([dataStr], { type: 'application/json' }), `${scanTemplate.name || 'scan-template'}.json`);
  };

  const group = currentView === 'group' ? (scanTemplateRoot.groupTemplates?.find(g => g.id === groupId) ?? null) : null;

  // if the group is not found, navigate to the overview page
  if (scanTemplateRoot.groupTemplates && groupId && !group) setTimeout(() => navigate(`/template/${scanTemplateId}`), 100);

  let mainContent: React.ReactNode;
  if (currentView === 'overview') {
    mainContent = (
      <ErrorBoundary componentName='ScanTemplateOverview'>
        <ScanTemplateOverview
          filteredGroups={filteredGroups}
          filteredTasksById={filteredTasksById}
          scanTemplateFilterFunctions={scanTemplateFilterFunctions}
          templateRoot={scanTemplateRoot}
          scanTemplate={scanTemplate}
          scanTemplateDataMutators={scanTemplateDataMutators}
          scanTemplateMutators={scanTemplateMutators}
          readOnly={readOnly}
        />
      </ErrorBoundary>
    );
  } else if (currentView === 'all-tasks') {
    mainContent = (
      <ErrorBoundary componentName='AllTemplateTasksEditor'>
        <AllTemplateTasksEditor
          allTasks={sortedTasks}
          filteredTasks={filteredTasks}
          selectedScanTemplatePartContext={selectedScanTemplatePartContext}
          setSelectedScanTemplatePartContext={setSelectedScanTemplatePartContext}
          filterRegex={filterRegex}
          templateRoot={scanTemplateRoot}
          scanTemplateDataMutators={scanTemplateDataMutators}
          readOnly={readOnly}
        />
      </ErrorBoundary>
    );
  } else if (group && currentView === 'group') {
    mainContent = (
      <ErrorBoundary componentName='ScanTemplateGroupEditor'>
        <ScanTemplateGroupEditor
          filteredTaskIds={filteredTasksByGroupIds[group.id] ?? []}
          scanTemplateId={scanTemplateId}
          scanTemplateDataMutators={scanTemplateDataMutators}
          group={group}
          selectedScanTemplatePartContext={selectedScanTemplatePartContext}
          setSelectedScanTemplatePartContext={setSelectedScanTemplatePartContext}
          filterRegex={filterRegex}
          templateRoot={scanTemplateRoot}
          readOnly={readOnly}
        />
      </ErrorBoundary>
    );
  }

  const downloadIconColor = COLORS2.alwaysDark.text.white;

  return (
    <PageLayout
      title={'Scan Template: ' + scanTemplate.name + ' (Beta)'}
      onBack={() => navigate('/templates')}
      headerButtons={downloadTemplateReady
        ? (
          <button
            onClick={downloadTemplate}
            type='button'
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: 8,
              color: downloadIconColor,
            }}
            title='Download Template JSON'
          >
            <DownloadIcon style={{ color: downloadIconColor }} />
          </button>
        )
        : undefined}
    >
      <div css={PANEL_CONTENT_STYLE}>
        <div css={LEFT_PANEL_STYLE}>
          <ScanTemplateNavPanel
            filteredTasks={filteredTasks}
            filteredGroups={filteredGroups}
            filteredTasksById={filteredTasksById}
            filter={filter}
            filterRegex={filterRegex}
            groupId={currentView === 'group' ? groupId : undefined}
            scanTemplate={scanTemplate}
            scanTemplateDataMutators={scanTemplateDataMutators}
            templateRoot={scanTemplateRoot}
            setCurrentGroupId={handleSetCurrentGroupId}
            setFilter={setFilter}
            setShowingAllTasks={setShowingAllTasks}
            setShowingTemplateOverview={setShowingTemplateOverview}
            showAllTasks={currentView === 'all-tasks'}
            readOnly={readOnly}
          />
        </div>
        <div css={MAIN_CONTENT_STYLE}>{mainContent}</div>
      </div>

      <FlyOut
        open={!!selectedScanTemplatePartContext}
        flyOutClosed={() => setSelectedScanTemplatePartContext(null)}
        title={`${TYPE_DISPLAY_NAME[getScanTemplatePartType(selectedScanTemplatePartContext ?? {}) ?? 'scanTemplate'] ?? ''}: ${
          selectedScanTemplatePart?.name ?? ''
        }`}
      >
        {selectedScanTemplatePartContext && (
          <ScanTemplatePartEditorPanel
            taskIdToGroupsMap={taskIdToGroupsMap}
            scanTemplatePartContext={selectedScanTemplatePartContext}
            setScanTemplatePartContext={setSelectedScanTemplatePartContext}
            scanTemplateRoot={scanTemplateRoot}
            scanTemplateDataMutators={scanTemplateDataMutators}
            taskCategories={taskCategories}
            groupCategories={groupCategories}
            onClose={() => setSelectedScanTemplatePartContext(null)}
            readOnly={readOnly}
          />
        )}
      </FlyOut>
    </PageLayout>
  );
};
